import React from 'react';
import { Check } from 'lucide-react';

const EngagementMetrics = () => {
  return (
    <section className="py-16 sm:py-24 px-3 sm:px-4 bg-gradient-to-br from-pink-50 to-rose-50 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-6 sm:gap-8 md:gap-16 items-center">
          {/* Chart Visual */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-3 sm:p-4 md:p-8 order-2 md:order-1">
            <div className="flex flex-col md:flex-row justify-between items-start mb-4 sm:mb-8 gap-3 sm:gap-4">
              <div className="flex-1 w-full">
                {/* Chart */}
                <div className="relative h-36 sm:h-48">
                  <svg viewBox="0 0 400 200" className="w-full h-full">
                    {/* Grid lines */}
                    <line x1="50" y1="40" x2="380" y2="40" stroke="#f3f4f6" strokeWidth="1" />
                    <line x1="50" y1="80" x2="380" y2="80" stroke="#f3f4f6" strokeWidth="1" />
                    <line x1="50" y1="120" x2="380" y2="120" stroke="#f3f4f6" strokeWidth="1" />
                    <line x1="50" y1="160" x2="380" y2="160" stroke="#f3f4f6" strokeWidth="1" />
                    
                    {/* Y-axis labels */}
                    <text x="30" y="45" fontSize="12" fill="#9ca3af">40</text>
                    <text x="30" y="85" fontSize="12" fill="#9ca3af">30</text>
                    <text x="30" y="125" fontSize="12" fill="#9ca3af">20</text>
                    <text x="30" y="165" fontSize="12" fill="#9ca3af">10</text>
                    
                    {/* X-axis labels */}
                    <text x="80" y="190" fontSize="12" fill="#9ca3af">Su</text>
                    <text x="130" y="190" fontSize="12" fill="#9ca3af">Mo</text>
                    <text x="180" y="190" fontSize="12" fill="#9ca3af">Tu</text>
                    <text x="230" y="190" fontSize="12" fill="#9ca3af">We</text>
                    <text x="280" y="190" fontSize="12" fill="#9ca3af">Th</text>
                    <text x="330" y="190" fontSize="12" fill="#9ca3af">Fr</text>
                    <text x="370" y="190" fontSize="12" fill="#9ca3af">Sa</text>
                    
                    {/* Pink line */}
                    <path d="M80,80 L130,75 L180,65 L230,60 L280,70 L330,65 L370,60" fill="none" stroke="#ec4899" strokeWidth="3" strokeLinecap="round" />
                    
                    {/* Cyan line */}
                    <path d="M80,140 L130,135 L180,100 L230,75 L280,90 L330,80 L370,75" fill="none" stroke="#06b6d4" strokeWidth="3" strokeLinecap="round" />
                    
                    {/* Dark line */}
                    <path d="M80,120 L130,115 L180,90 L230,85 L280,80 L330,85 L370,80" fill="none" stroke="#1f2937" strokeWidth="3" strokeLinecap="round" />
                  </svg>
                </div>
              </div>
              
              {/* Stats Card */}
              <div className="bg-gradient-to-br from-pink-500 to-rose-500 rounded-lg sm:rounded-xl p-3 sm:p-4 w-full md:w-auto md:ml-4">
                <p className="text-[10px] sm:text-xs text-pink-100 uppercase mb-1">Audience</p>
                <p className="text-xl sm:text-2xl font-bold text-white">2.5K</p>
              </div>
            </div>
            
            {/* Legend */}
            <div className="flex flex-wrap gap-3 sm:gap-4 md:gap-6">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-2.5 sm:w-3 h-2.5 sm:h-3 bg-pink-500 rounded-full"></div>
                <span className="text-xs sm:text-sm text-gray-600">Telegram</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-2.5 sm:w-3 h-2.5 sm:h-3 bg-cyan-500 rounded-full"></div>
                <span className="text-xs sm:text-sm text-gray-600">WhatsApp</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-2.5 sm:w-3 h-2.5 sm:h-3 bg-gray-800 rounded-full"></div>
                <span className="text-xs sm:text-sm text-gray-600">Total</span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="order-1 md:order-2">
            <p className="text-pink-500 font-medium mb-3 sm:mb-4 uppercase tracking-wide text-xs sm:text-sm">Engagement Metrics</p>
            <h2 className="text-xl sm:text-3xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 md:mb-8">
              User Engagement<br /><span className="italic">Tracking</span>
            </h2>
            
            <div className="space-y-4 sm:space-y-8">
              <div className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-sm">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">Real-Time Analytics</h3>
                <p className="text-gray-600 text-sm sm:text-base mb-2 sm:mb-3">
                  Get real-time insights into user interactions with your chatbots.
                </p>
                <a href="https://app.waatee.com/register" className="text-pink-500 font-medium hover:text-pink-600 transition-colors text-xs sm:text-sm">
                  Sign Up 14 Days Trial →
                </a>
              </div>
              
              <div className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-sm">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">Response Management</h3>
                <p className="text-gray-600 text-sm sm:text-base mb-2 sm:mb-3">
                  Efficiently respond to user inquiries and track engagement with an intuitive interface.
                </p>
                <a href="https://app.waatee.com/register" className="text-pink-500 font-medium hover:text-pink-600 transition-colors text-xs sm:text-sm">
                  Sign Up 14 Days Trial →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EngagementMetrics;
